import type { EFFeatureDefinition } from "@/features/runtime/FeatureDefinition";
import type {
	EFFeatureActivationState,
	EFFeatureDockTarget,
	EFFeatureEligibilityState,
	EFFeatureId,
	EFFeatureRuntime,
} from "@/features/runtime/FeatureTypes";

function freezeEligibilityState(
	state: EFFeatureEligibilityState
): EFFeatureEligibilityState {
	return Object.freeze({
		eligible: state.eligible,
		reasons: Object.freeze([...state.reasons]),
	});
}

function freezeActivationState(
	state: EFFeatureActivationState
): EFFeatureActivationState {
	return Object.freeze({
		active: state.active,
		visible: state.visible,
	});
}

function matchesFeatureDockTarget(
	definition: EFFeatureDefinition,
	runtime: EFFeatureRuntime
): boolean {
	if (!definition.defaultDockTarget) {
		return false;
	}

	return runtime.view.shell.activeDockTarget === definition.defaultDockTarget;
}

export function createEFFeatureRegistry(
	definitions: readonly EFFeatureDefinition[]
): ReadonlyMap<EFFeatureId, EFFeatureDefinition> {
	const entries = new Map<EFFeatureId, EFFeatureDefinition>();

	for (const definition of definitions) {
		if (entries.has(definition.id)) {
			throw new Error(
				`[FeatureRegistry] duplicate feature definition id '${definition.id}'`
			);
		}

		entries.set(definition.id, Object.freeze({ ...definition }));
	}

	return entries;
}

export function getRequiredFeatureDefinition(
	registry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>,
	featureId: EFFeatureId
): EFFeatureDefinition {
	const definition = registry.get(featureId);

	if (!definition) {
		throw new Error(
			`[FeatureRegistry] unknown feature '${featureId}' requested from registry`
		);
	}

	return definition;
}

export function resolveFeatureIdForDockTarget(
	registry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>,
	dockTarget: EFFeatureDockTarget | null
): EFFeatureId | null {
	if (!dockTarget) {
		return null;
	}

	for (const definition of registry.values()) {
		if (definition.defaultDockTarget === dockTarget) {
			return definition.id;
		}
	}

	return null;
}

export function resolveFeatureEligibility(
	definition: EFFeatureDefinition,
	runtime: EFFeatureRuntime
): EFFeatureEligibilityState {
	const reasons: string[] = [];
	const requirements = definition.eligibility;

	if (requirements.requiresHostConfigured && !runtime.view.host.configured) {
		reasons.push("host_not_configured");
	}

	if (requirements.requiresGameReady && !runtime.view.host.gameReady) {
		reasons.push("host_game_not_ready");
	}

	if (requirements.requiresProviderReady && !runtime.view.provider.ready) {
		reasons.push("provider_not_ready");
	}

	if (
		requirements.supportedProviders &&
		requirements.supportedProviders.length > 0
	) {
		const providerId = runtime.view.provider.providerId;

		if (!providerId) {
			reasons.push("provider_missing");
		} else if (!requirements.supportedProviders.includes(providerId)) {
			reasons.push("provider_not_supported");
		}
	}

	return freezeEligibilityState({
		eligible: reasons.length === 0,
		reasons,
	});
}

export function resolveFeatureActivation(
	definition: EFFeatureDefinition,
	runtime: EFFeatureRuntime,
	eligibility: EFFeatureEligibilityState
): EFFeatureActivationState {
	if (!eligibility.eligible) {
		return freezeActivationState({
			active: false,
			visible: false,
		});
	}

	const selectedByShell = matchesFeatureDockTarget(definition, runtime);
	const ownsPopup = runtime.view.selection.popupFeatureId === definition.id;

	const dockActive =
		definition.capabilities.usesDock &&
		selectedByShell &&
		!runtime.view.shell.popupVisible;

	const popupActive =
		definition.capabilities.usesPopup &&
		runtime.view.shell.popupVisible &&
		ownsPopup;

	const active = dockActive || popupActive;
	const visible = active;

	return freezeActivationState({
		active,
		visible,
	});
}